/**
 * SuggestionCard — Day Diary v2 reclamation-suggestion card (task 237, D2).
 *
 * Renders one fallback-batch suggestion as a five-section card:
 *
 *   1. Captured        — original Event metadata (time, app, title, url, host).
 *                        Missing fields render `—`.
 *   2. What I noticed   — the `observed` prose + a chip row of `observed_text`
 *      on screen          tokens (clickable copy-to-clipboard, AC8). GATED on
 *                         `canViewScreenshots` (§2.7 item 4, AC9).
 *   3. Proposed         — variant-specific attribution chain. `unattributable`
 *      attribution        shows the assistant-voice "I'm not sure where this
 *                         fits" with no chain.
 *   4. Confidence       — pill, semantic color (green ≥ 0.8, amber 0.5–0.8,
 *                         gray < 0.5).
 *   5. Actions          — Accept / Dismiss / View screenshot (always).
 *                         `new_activity` adds "Edit before accepting".
 *                         `unattributable` has NO Accept (Dismiss + "Mark for
 *                         manual review" only).
 *
 * §2.7 SEAM (AC9): `canViewScreenshots` is the screenshot-view gate. When
 * `false`, section 2 (the OCR `observed` prose + `observed_text` chips) is
 * omitted entirely. Phase 1 feeds this prop from the existing screenshot-view
 * gate (org member → true); Phase 2 (roadmap, not this task) swaps the input
 * to `viewerIsSelfOrOrgAdmin` — same component, same test, stricter input.
 *
 * Presentational: data fetching + the accept/dismiss mutations live in the
 * `useDayDiary` hook; this component takes callbacks.
 */

import { useState } from 'react';
import { Button } from '@/components/primitives/Button';
import { DAY_DIARY_COPY } from '@/lib/dayDiaryCopy';
import { formatTimeInTz } from '@/lib/dates';
import type { DiarySuggestion, DiarySuggestionVariant } from '@/types/dayDiaryV2';
import styles from './SuggestionCard.module.css';

const EMPTY = '—';

const VARIANT_LABEL: Record<DiarySuggestionVariant, string> = {
  existing_activity: 'Existing activity',
  new_activity: 'New activity',
  personal: 'Personal',
  unattributable: 'Unattributable',
};

/** Semantic confidence band → pill class. */
function confidenceTone(confidence: number): 'high' | 'medium' | 'low' {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}

export interface SuggestionCardProps {
  suggestion: DiarySuggestion;
  /**
   * §2.7 item 4 / AC9 — screenshot-view capability. When `false`, the OCR
   * section (the `observed` prose + `observed_text` chips) is NOT rendered.
   */
  canViewScreenshots: boolean;
  /** IANA timezone for the captured-time render. */
  tz: string;
  /** POSTs to task 236's accept route + refreshes. Omitted for unattributable. */
  onAccept?: (suggestion: DiarySuggestion) => void;
  /** `new_activity` only — open the edit-before-accepting affordance. */
  onEditBeforeAccepting?: (suggestion: DiarySuggestion) => void;
  /** POSTs to task 236's dismiss route + refreshes. */
  onDismiss: (suggestion: DiarySuggestion) => void;
  /** Opens the existing screenshot lightbox surface. */
  onViewScreenshot?: (suggestion: DiarySuggestion) => void;
  /** `unattributable` only — flag for manual review. */
  onMarkForReview?: (suggestion: DiarySuggestion) => void;
  /** Disables the action buttons while a mutation is in flight. */
  busy?: boolean;
}

export function SuggestionCard({
  suggestion,
  canViewScreenshots,
  tz,
  onAccept,
  onEditBeforeAccepting,
  onDismiss,
  onViewScreenshot,
  onMarkForReview,
  busy = false,
}: SuggestionCardProps) {
  const { captured, attribution, variant } = suggestion;
  const tone = confidenceTone(suggestion.confidence);
  const isUnattributable = variant === 'unattributable';
  const isNewActivity = variant === 'new_activity';

  return (
    <div className={styles.card} data-testid="suggestion-card" data-variant={variant}>
      <div className={styles.cardHeader}>
        <span className={styles.variantLabel}>{VARIANT_LABEL[variant]}</span>
        <ConfidencePill confidence={suggestion.confidence} tone={tone} />
      </div>

      {/* Section 1 — Captured */}
      <section className={styles.section} aria-labelledby={`captured-${suggestion.id}`}>
        <h4 id={`captured-${suggestion.id}`} className={styles.sectionTitle}>
          Captured
        </h4>
        <dl className={styles.metaGrid}>
          <MetaRow label="Time" value={fmtTime(captured.occurredAt, tz)} />
          <MetaRow label="App" value={captured.app} />
          <MetaRow label="Title" value={captured.title} />
          <MetaRow label="URL" value={captured.url} />
          <MetaRow label="Host" value={captured.host} />
        </dl>
      </section>

      {/* Section 2 — What I noticed on screen (GATED on canViewScreenshots) */}
      {canViewScreenshots && (
        <section
          className={styles.section}
          aria-labelledby={`observed-${suggestion.id}`}
          data-testid="suggestion-card-observed"
        >
          <h4 id={`observed-${suggestion.id}`} className={styles.sectionTitle}>
            {DAY_DIARY_COPY.OCR_SECTION_HEADER}
          </h4>
          {suggestion.lowSignal ? (
            <p className={styles.lowSignal}>{DAY_DIARY_COPY.LOW_SIGNAL_FRAME}</p>
          ) : (
            <>
              {suggestion.observed ? (
                <p className={styles.observedProse}>{suggestion.observed}</p>
              ) : (
                <p className={styles.empty}>{EMPTY}</p>
              )}
              {suggestion.observedText.length > 0 && (
                <ObservedTextChips tokens={suggestion.observedText} />
              )}
            </>
          )}
        </section>
      )}

      {/* Section 3 — Proposed attribution */}
      <section className={styles.section} aria-labelledby={`attribution-${suggestion.id}`}>
        <h4 id={`attribution-${suggestion.id}`} className={styles.sectionTitle}>
          Proposed attribution
        </h4>
        {isUnattributable || !attribution ? (
          <p className={styles.unattributable}>{DAY_DIARY_COPY.UNATTRIBUTABLE_LABEL}</p>
        ) : (
          <p className={styles.attributionChain}>
            <span className={styles.attributionActivity}>{attribution.activityName ?? EMPTY}</span>
            <span aria-hidden="true" className={styles.chevron}>
              {' → '}
            </span>
            <span className={styles.attributionTarget}>
              {attribution.clientName ?? EMPTY}
              {' / '}
              {attribution.projectName ?? (variant === 'new_activity' ? 'no project' : EMPTY)}
            </span>
            {attribution.notBillable && <span className={styles.notBillable}> — not billable</span>}
          </p>
        )}
      </section>

      {/* Section 5 — Actions (Confidence pill is rendered in the header) */}
      <div className={styles.actions}>
        {!isUnattributable && onAccept && (
          <Button
            variant="primary"
            size="sm"
            disabled={busy}
            onClick={() => onAccept(suggestion)}
            data-testid="suggestion-card-accept"
          >
            Accept
          </Button>
        )}
        {isNewActivity && onEditBeforeAccepting && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => onEditBeforeAccepting(suggestion)}
            data-testid="suggestion-card-edit"
          >
            Edit before accepting
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={() => onDismiss(suggestion)}
          data-testid="suggestion-card-dismiss"
        >
          Dismiss
        </Button>
        {isUnattributable && onMarkForReview && (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => onMarkForReview(suggestion)}
            data-testid="suggestion-card-mark-review"
          >
            Mark for manual review
          </Button>
        )}
        {suggestion.screenshotId && onViewScreenshot && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewScreenshot(suggestion)}
            data-testid="suggestion-card-view-screenshot"
          >
            View screenshot
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function MetaRow({ label, value }: { label: string; value: string | null }) {
  return (
    <>
      <dt className={styles.metaLabel}>{label}</dt>
      <dd className={styles.metaValue}>{value && value.length > 0 ? value : EMPTY}</dd>
    </>
  );
}

function ConfidencePill({
  confidence,
  tone,
}: {
  confidence: number;
  tone: 'high' | 'medium' | 'low';
}) {
  return (
    <span
      className={`${styles.confidencePill} ${styles[`confidence_${tone}`]}`}
      data-testid="suggestion-card-confidence"
    >
      {Math.round(confidence * 100)}%
    </span>
  );
}

function ObservedTextChips({ tokens }: { tokens: string[] }) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = (token: string) => {
    void navigator.clipboard?.writeText(token);
    setCopied(token);
  };

  return (
    <div className={styles.chipRow} data-testid="suggestion-card-chips">
      {tokens.map((token, i) => (
        <button
          key={`${token}-${i}`}
          type="button"
          className={styles.chip}
          onClick={() => copy(token)}
          title="Copy to clipboard"
          aria-label={`Copy "${token}" to clipboard`}
          data-testid="suggestion-card-chip"
        >
          {token}
          {copied === token && <span className={styles.chipCopied}> ✓</span>}
        </button>
      ))}
    </div>
  );
}

function fmtTime(iso: string | null, tz: string): string | null {
  if (!iso) return null;
  return formatTimeInTz(iso, tz);
}
